/**
 * FrameWrapper - 包裝 trait/state/chart 元件，根據 preset 或 customStyle 注入 CSS 變數
 */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { CSSProperties } from 'react'
import type { FramePreset } from '../../services/appfactoryFirestore'
import { getFramerPresetOverrides } from '../../services/appfactoryApi'
import { getFrameStyle } from './framePresets'
import type { FrameStyleVars } from './framePresets'

let framerOverridesCache: Record<string, FrameStyleVars> | null = null
let framerOverridesLoading: Promise<Record<string, FrameStyleVars>> | null = null

async function loadFramerOverrides(): Promise<Record<string, FrameStyleVars>> {
  if (framerOverridesCache) return framerOverridesCache
  if (!framerOverridesLoading) {
    framerOverridesLoading = getFramerPresetOverrides()
      .then((overrides) => {
        framerOverridesCache = (overrides ?? {}) as Record<string, FrameStyleVars>
        return framerOverridesCache
      })
      .catch(() => {
        framerOverridesCache = {}
        return framerOverridesCache
      })
      .finally(() => {
        framerOverridesLoading = null
      })
  }
  return framerOverridesLoading
}

export default function FrameWrapper({
  preset,
  customStyle,
  children,
}: {
  preset: FramePreset | string
  /** 視覺編輯器覆蓋，優先於 preset；自訂 preset 必須提供 */
  customStyle?: FrameStyleVars
  children: ReactNode
}) {
  const [framerOverrides, setFramerOverrides] = useState<Record<string, FrameStyleVars>>(
    () => framerOverridesCache ?? {}
  )

  useEffect(() => {
    let cancelled = false
    const sync = async () => {
      const next = await loadFramerOverrides()
      if (!cancelled) setFramerOverrides(next)
    }
    void sync()

    const refresh = () => {
      framerOverridesCache = null
      void sync()
    }
    window.addEventListener('appfactory-framer-presets-refresh', refresh)
    return () => {
      cancelled = true
      window.removeEventListener('appfactory-framer-presets-refresh', refresh)
    }
  }, [])

  const baseStyle = getFrameStyle(preset as FramePreset) as CSSProperties | undefined
  const overrideStyle = framerOverrides[String(preset)] as CSSProperties | undefined
  const style = customStyle
    ? (customStyle as CSSProperties)
    : ({ ...(baseStyle ?? {}), ...(overrideStyle ?? {}) } as CSSProperties)
  if (!style) return <>{children}</>
  return (
    <div className="trait-frame-wrapper" style={style}>
      {children}
    </div>
  )
}
